/**
 * GanttChart — React wrapper for Frappe Gantt.
 *
 * Renders an SVG-based Gantt chart from a GanttTask array.
 * Supports view mode switching, click-to-select sync, and theming.
 *
 * Usage:
 *   <GanttChart
 *     tasks={tasks}
 *     viewMode="Month"
 *     onTaskClick={(wbs) => setSelected(wbs)}
 *   />
 *
 * Epic: DEV-396, Task: DEV-401
 */

import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import Gantt, { FrappeTask } from 'frappe-gantt';
import { extractPredecessorTaskIds, parsePredecessors } from '../GanttPanel/predecessorParser';
// Frappe Gantt CSS is copied to public/ and loaded via <link> in index.html
// because the package's restrictive "exports" field blocks all CSS imports.
import type { GanttTask } from '../../api/ganttTasks';
import type { BaselineTask } from '../../api/ganttBaselines';
import styles from './GanttChart.module.css';

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

export type GanttColorTheme = 'black' | 'white' | 'match_theme';

export interface GanttChartProps {
  /** Task data from the API / editor draft state. */
  tasks: GanttTask[];
  /** Zoom level — maps directly to Frappe Gantt view modes. */
  viewMode?: 'Day' | 'Week' | 'Month' | 'Year';
  /** Fires when the user clicks a bar; returns the task's sequential ID. */
  onTaskClick?: (taskId: number) => void;
  /** taskId of the currently selected task — highlights the bar. */
  selectedTaskId?: number | null;
  /** Color theme for the Gantt bars: black, white, or match_theme (default). */
  colorTheme?: GanttColorTheme;
  /** DEV-565: When true, critical path tasks are highlighted with a red border. */
  showCriticalPath?: boolean;
  /** DEV-564: Baseline task data for ghost bar overlay. */
  baselineTasks?: BaselineTask[];
  /** DEV-564: When true, baseline ghost bars are shown behind live bars. */
  showBaseline?: boolean;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

/** Add one calendar day to an ISO date string. */
function addOneDay(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

/**
 * Convert our GanttTask to Frappe Gantt's task shape.
 *
 * Tasks without dates get a 1-day invisible placeholder so Frappe
 * reserves their row slot, keeping the chart row-aligned with the grid.
 * The placeholder bar is hidden via CSS (.gantt-placeholder).
 */
function toFrappeTask(task: GanttTask, allTasks: GanttTask[]): FrappeTask {
  const hasValidDates = !!task.startDate && !!task.endDate;

  if (!hasValidDates) {
    // Create an invisible placeholder that occupies a row.
    // Use a nearby task's date range (or today) so the placeholder
    // doesn't distort the chart's overall date range.
    const fallbackDate = findNearestDate(task, allTasks) ?? todayISO();
    return {
      id: String(task.taskId),
      name: task.taskName || ' ',
      start: fallbackDate,
      end: addOneDay(fallbackDate),
      progress: 0,
      dependencies: '',
      custom_class: 'gantt-placeholder',
      _wbs: task.wbs,
      _taskId: task.taskId,
      _indentLevel: task.indentLevel,
      _isMilestone: false,
      _isCritical: false,
      _totalFloat: null,
      _parsedPredecessors: [],
    };
  }

  // Milestones are zero-duration; Frappe needs start ≠ end to render a bar,
  // so we give it a 1-day span. The post-render diamond patch replaces the
  // rect visually, but the bar-wrapper still needs a measurable width for
  // Frappe's internal layout and arrow anchor calculations.
  const isMilestone = !!task.isMilestone;
  const effectiveEnd = isMilestone ? addOneDay(task.startDate!) : task.endDate!;
  return {
    id: String(task.taskId),
    name: task.taskName || task.wbs || String(task.taskId),
    start: task.startDate!,
    end: effectiveEnd,
    progress: isMilestone ? 0 : (task.percentComplete ?? 0),
    // Frappe expects plain comma-separated taskId numbers for arrow rendering.
    // Extract just the IDs, stripping dependency type suffixes (SS, FF, SF) and lag.
    dependencies: extractPredecessorTaskIds(task.predecessors).join(', ') || '',
    custom_class: [
      isMilestone ? 'gantt-milestone' : '',
      task.isSummary ? 'gantt-summary' : '',
    ]
      .filter(Boolean)
      .join(' ') || undefined,
    // Carry along metadata for click handler and post-render patching
    _wbs: task.wbs,
    _taskId: task.taskId,
    _indentLevel: task.indentLevel,
    _isMilestone: isMilestone,
    _isCritical: task.isCritical ?? false,
    _totalFloat: task.totalFloat ?? null,
    _parsedPredecessors: parsePredecessors(task.predecessors),
  };
}

/** Find the nearest sibling task's start date for placeholder positioning. */
function findNearestDate(task: GanttTask, allTasks: GanttTask[]): string | null {
  const idx = allTasks.indexOf(task);
  // Search outward from the task's position
  for (let offset = 1; offset < allTasks.length; offset++) {
    const before = idx - offset >= 0 ? allTasks[idx - offset] : null;
    const after = idx + offset < allTasks.length ? allTasks[idx + offset] : null;
    if (before?.startDate) return before.startDate;
    if (after?.startDate) return after.startDate;
  }
  return null;
}

/** Return today's date as ISO string. */
function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Post-render patch: replace milestone bar <rect> elements with diamond
 * <polygon> markers. Called after every Frappe Gantt render/refresh.
 *
 * Frappe renders every task as a rectangular bar. For milestones we:
 * 1. Find the .bar-wrapper with the gantt-milestone class
 * 2. Get the bar rect's center position (cx, cy)
 * 3. Replace the rect with a rotated-square polygon (diamond)
 * 4. Hide the progress bar (milestones have no progress)
 * 5. Reposition the label to the right of the diamond
 */
function patchMilestoneDiamonds(container: HTMLElement): void {
  const milestoneWrappers = container.querySelectorAll('.bar-wrapper.gantt-milestone');
  const DIAMOND_SIZE = 10; // half-width of the diamond (total = 20px)

  milestoneWrappers.forEach((wrapper) => {
    const barRect = wrapper.querySelector('.bar') as SVGRectElement | null;
    if (!barRect) return;

    // Already patched — polygon replaces rect, check for our marker attribute
    if (wrapper.querySelector('.milestone-diamond')) return;

    // Get the rect's position — use the center as the diamond anchor point
    const x = parseFloat(barRect.getAttribute('x') || '0');
    const y = parseFloat(barRect.getAttribute('y') || '0');
    const width = parseFloat(barRect.getAttribute('width') || '0');
    const height = parseFloat(barRect.getAttribute('height') || '0');
    const cx = x + width / 2;
    const cy = y + height / 2;

    // Create diamond polygon: four points forming a rotated square
    const points = [
      `${cx},${cy - DIAMOND_SIZE}`,       // top
      `${cx + DIAMOND_SIZE},${cy}`,        // right
      `${cx},${cy + DIAMOND_SIZE}`,        // bottom
      `${cx - DIAMOND_SIZE},${cy}`,        // left
    ].join(' ');

    const ns = 'http://www.w3.org/2000/svg';
    const diamond = document.createElementNS(ns, 'polygon');
    diamond.setAttribute('points', points);
    diamond.setAttribute('class', 'milestone-diamond');
    // Inherit fill from CSS (.gantt-milestone .milestone-diamond)
    diamond.style.fill = 'var(--accent-warning, #f39c12)';
    diamond.style.stroke = 'none';

    // Insert diamond before the rect, then hide the rect + progress bar
    barRect.parentNode?.insertBefore(diamond, barRect);
    barRect.style.display = 'none';

    const progressBar = wrapper.querySelector('.bar-progress') as SVGElement | null;
    if (progressBar) progressBar.style.display = 'none';

    // Reposition label to the right of the diamond
    const label = wrapper.querySelector('.bar-label') as SVGTextElement | null;
    if (label) {
      label.setAttribute('x', String(cx + DIAMOND_SIZE + 6));
      label.setAttribute('y', String(cy + 4));
      label.style.fill = 'var(--text-primary)';
      label.style.dominantBaseline = 'auto';
    }
  });
}

/**
 * DEV-565: Post-render patch — add/remove critical path highlighting
 * on bar wrappers. When showCriticalPath is true, bars for critical
 * tasks get a `gantt-critical` CSS class for red border styling.
 */
function patchCriticalPath(
  container: HTMLElement,
  frappeTasks: FrappeTask[],
  show: boolean
): void {
  // Build lookup: taskId → isCritical
  const criticalSet = new Set<string>();
  if (show) {
    for (const ft of frappeTasks) {
      if ((ft as any)._isCritical) criticalSet.add(ft.id);
    }
  }

  const wrappers = container.querySelectorAll('.bar-wrapper');
  wrappers.forEach((wrapper) => {
    const dataId = wrapper.getAttribute('data-id');
    if (dataId && criticalSet.has(dataId)) {
      wrapper.classList.add('gantt-critical');
    } else {
      wrapper.classList.remove('gantt-critical');
    }
  });
}

/**
 * DEV-565: Post-render patch — color dependency arrows red when both
 * endpoints are on the critical path. Frappe Gantt renders arrows as
 * SVG <path> elements with data-from / data-to attributes inside the
 * arrow layer group. The arrowhead is drawn as part of the same path
 * (inline `m -5 -5 l 5 5 l -5 5`), so styling stroke + fill on the
 * path covers both the connector line and the arrowhead triangle.
 */
function patchCriticalPathArrows(
  container: HTMLElement,
  frappeTasks: FrappeTask[],
  show: boolean
): void {
  const criticalSet = new Set<string>();
  if (show) {
    for (const ft of frappeTasks) {
      if ((ft as any)._isCritical) criticalSet.add(ft.id);
    }
  }

  // Arrow paths live inside the SVG; query all paths with data-from/data-to
  const arrows = container.querySelectorAll('path[data-from][data-to]');
  arrows.forEach((arrow) => {
    const fromId = arrow.getAttribute('data-from');
    const toId = arrow.getAttribute('data-to');
    if (fromId && toId && criticalSet.has(fromId) && criticalSet.has(toId)) {
      arrow.classList.add('gantt-critical-arrow');
    } else {
      arrow.classList.remove('gantt-critical-arrow');
    }
  });
}

/**
 * DEV-564: Post-render patch — inject baseline ghost bars behind live bars.
 *
 * Strategy: For each live bar, look up the matching baseline task by taskId.
 * Use the Frappe Gantt instance's internal column_width and gantt_start to
 * compute pixel positions for baseline dates. Inject a semi-transparent rect
 * behind the live bar. If the task is late (actual end > baseline end), add
 * a red variance marker at the baseline end position.
 */
function patchBaselineGhostBars(
  container: HTMLElement,
  ganttInstance: Gantt | null,
  baselineTasks: BaselineTask[],
  show: boolean
): void {
  // Remove any existing ghost bars first
  container.querySelectorAll('.baseline-ghost, .baseline-variance').forEach((el) => el.remove());

  if (!show || !ganttInstance || baselineTasks.length === 0) return;

  // Build lookup: taskId → baseline
  const baselineMap = new Map<string, BaselineTask>();
  for (const bt of baselineTasks) {
    baselineMap.set(String(bt.taskId), bt);
  }

  // Access Frappe Gantt internals for date-to-pixel conversion
  const gantt = ganttInstance as any;
  const ganttStart = gantt.gantt_start; // Date object — earliest date on the timescale
  const columnWidth = gantt.options?.column_width; // pixels per time unit
  const step = gantt.options?.step; // hours per column (24 for Day, 168 for Week, etc.)

  if (!ganttStart || !columnWidth || !step) return;

  const hoursPerPixel = step / columnWidth;
  const ganttStartMs = new Date(ganttStart).getTime();

  /** Convert a date string to an x pixel coordinate */
  const dateToX = (dateStr: string): number => {
    const ms = new Date(dateStr + 'T00:00:00').getTime();
    const diffHours = (ms - ganttStartMs) / (1000 * 60 * 60);
    return diffHours / hoursPerPixel;
  };

  const ns = 'http://www.w3.org/2000/svg';

  const wrappers = container.querySelectorAll('.bar-wrapper');
  wrappers.forEach((wrapper) => {
    const dataId = wrapper.getAttribute('data-id');
    if (!dataId) return;

    const baseline = baselineMap.get(dataId);
    if (!baseline || !baseline.startDate || !baseline.endDate) return;

    // Get the live bar's y position
    const barRect = wrapper.querySelector('.bar') as SVGRectElement | null;
    if (!barRect) return;

    const barY = parseFloat(barRect.getAttribute('y') || '0');
    const barHeight = parseFloat(barRect.getAttribute('height') || '24');

    // Compute ghost bar position from baseline dates
    const ghostX = dateToX(baseline.startDate);
    const ghostEndX = dateToX(baseline.endDate);
    const ghostWidth = Math.max(ghostEndX - ghostX, 2);

    // Create ghost bar — sits behind the live bar, slightly taller
    const ghost = document.createElementNS(ns, 'rect');
    ghost.setAttribute('x', String(ghostX));
    ghost.setAttribute('y', String(barY - 1));
    ghost.setAttribute('width', String(ghostWidth));
    ghost.setAttribute('height', String(barHeight + 2));
    ghost.setAttribute('rx', '3');
    ghost.setAttribute('ry', '3');
    ghost.setAttribute('class', 'baseline-ghost');
    ghost.style.fill = 'var(--text-muted, #888)';
    ghost.style.opacity = '0.2';
    ghost.style.pointerEvents = 'none';

    // Insert ghost BEFORE the bar so it renders behind
    const barGroup = barRect.parentElement;
    if (barGroup) {
      barGroup.insertBefore(ghost, barGroup.firstChild);
    }

    // Variance marker — red line at baseline end if task is late
    const liveBar = wrapper.querySelector('.bar') as SVGRectElement | null;
    if (liveBar) {
      const liveEndX = parseFloat(liveBar.getAttribute('x') || '0') +
                        parseFloat(liveBar.getAttribute('width') || '0');
      if (liveEndX > ghostEndX + 2) {
        // Task is late — add red variance marker at baseline end
        const marker = document.createElementNS(ns, 'line');
        marker.setAttribute('x1', String(ghostEndX));
        marker.setAttribute('y1', String(barY - 3));
        marker.setAttribute('x2', String(ghostEndX));
        marker.setAttribute('y2', String(barY + barHeight + 3));
        marker.setAttribute('class', 'baseline-variance');
        marker.style.stroke = '#ef4444';
        marker.style.strokeWidth = '2';
        marker.style.pointerEvents = 'none';
        barGroup?.appendChild(marker);
      }
    }
  });
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function GanttChart({
  tasks,
  viewMode = 'Month',
  onTaskClick,
  selectedTaskId,
  colorTheme = 'match_theme',
  showCriticalPath = false,
  baselineTasks = [],
  showBaseline = false,
}: GanttChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const ganttRef = useRef<Gantt | null>(null);
  const prevViewModeRef = useRef(viewMode);

  // Stable callback ref for click handler
  const onTaskClickRef = useRef(onTaskClick);
  onTaskClickRef.current = onTaskClick;

  // Convert tasks — dateless tasks become invisible placeholders to
  // keep chart rows aligned with the grid.
  const frappeTasks = useMemo(
    () => tasks.map((t) => toFrappeTask(t, tasks)),
    [tasks]
  );

  // ── Destroy helper ─────────────────────────────────────────────
  const destroyChart = useCallback(() => {
    if (containerRef.current) {
      containerRef.current.innerHTML = '';
    }
    ganttRef.current = null;
  }, []);

  // ── Cleanup on unmount ─────────────────────────────────────────
  useEffect(() => {
    return () => { destroyChart(); };
  }, [destroyChart]);

  // ── Scroll to today / first bar ──────────────────────────────
  /** After Frappe Gantt renders, scroll the container so the today
   *  marker (or the first bar if no today marker) is visible and
   *  roughly centred horizontally. */
  const scrollToToday = useCallback(() => {
    if (!containerRef.current) return;

    // Use a short timeout so Frappe Gantt finishes its internal
    // layout calculations (SVG sizing, bar positioning) before we
    // try to measure element positions and scroll.
    setTimeout(() => {
      if (!containerRef.current) return;
      // Frappe Gantt creates a .gantt-container child that handles overflow
      const gc = containerRef.current.querySelector('.gantt-container') as HTMLElement | null;
      if (!gc) return;

      // Prefer the today marker (.current-highlight), fall back to the first bar
      const todayMarker = gc.querySelector('.current-highlight') as HTMLElement | null;
      const firstBar = gc.querySelector('.bar-wrapper .bar') as SVGElement | null;

      let targetX: number | null = null;
      if (todayMarker) {
        // .current-highlight uses inline style left (e.g. "3662.4px")
        targetX = parseFloat(todayMarker.style.left || todayMarker.getAttribute('x') || '');
      }
      if ((targetX == null || isNaN(targetX)) && firstBar) {
        targetX = parseFloat(firstBar.getAttribute('x') || '');
      }
      if (targetX == null || isNaN(targetX)) return;

      // Centre the target in the visible area
      const scrollTarget = Math.max(0, targetX - gc.clientWidth / 2);
      gc.scrollLeft = scrollTarget;
    }, 150);
  }, []);

  // ── Initialize / Refresh ─────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return;

    // Need at least one task for Frappe Gantt to render
    if (frappeTasks.length === 0) {
      destroyChart();
      return;
    }

    // Always destroy and recreate when task list changes.
    // Frappe Gantt's refresh() has internal state bugs when the task
    // count changes (stale _index values, arrow anchors, grid rows).
    // Recreating is fast and avoids subtle rendering glitches.
    if (ganttRef.current) {
      destroyChart();
    }

    if (!ganttRef.current) {
      // First render or re-create after failed refresh
      containerRef.current.innerHTML = '';
      try {
        ganttRef.current = new Gantt(containerRef.current, frappeTasks, {
          readonly: true,
          readonly_dates: true,
          readonly_progress: true,
          view_mode: viewMode,
          bar_height: 24,
          bar_corner_radius: 3,
          padding: 14,
          container_height: 'auto',
          today_button: false,
          view_mode_select: false,
          lines: 'both',
          // Performance: disable infinite_padding to prevent full chart
          // re-renders when scrolling near edges. The default (true) calls
          // render() on mousewheel near boundaries, causing major lag.
          infinite_padding: false,
          // Performance: keep auto_move_label disabled (default). When
          // enabled it calls getBBox() on every bar on every scroll pixel.
          auto_move_label: false,
          on_click: (task: FrappeTask) => {
            const tid = (task as any)._taskId ?? parseInt(task.id, 10);
            onTaskClickRef.current?.(tid);
          },
        });

        // ── Performance patches ──────────────────────────────────
        // Frappe Gantt's unthrottled scroll handler does expensive work
        // on every scroll pixel. We patch two things:
        //
        // 1. Cache get_start_end_positions() — it calls getBBox() on
        //    every bar (O(n) forced layouts). Cache until render().
        //
        // 2. Intercept scroll events on .gantt-container so that
        //    vertical-only scrolls (e.g. from our grid sync) don't
        //    trigger Frappe's horizontal date recalculation at all.
        //    This prevents the "timescale jumps" issue and avoids
        //    wasted CPU on date math + DOM queries during vertical scroll.
        const gantt = ganttRef.current as any;
        if (gantt && typeof gantt.get_start_end_positions === 'function') {
          const origGetPositions = gantt.get_start_end_positions.bind(gantt);
          let cachedPositions: [number, number, number] | null = null;
          gantt.get_start_end_positions = () => {
            if (!cachedPositions) {
              cachedPositions = origGetPositions();
            }
            return cachedPositions;
          };
          // Invalidate cache when chart re-renders (view mode change, etc.)
          const origRender = gantt.render?.bind(gantt);
          if (origRender) {
            gantt.render = (...args: any[]) => {
              cachedPositions = null;
              return origRender(...args);
            };
          }
        }

        // Note: Frappe's scroll handler still fires on vertical-only scrolls
        // but the expensive get_start_end_positions() call is cached (patch 1
        // above) and infinite_padding is disabled, so the overhead is minimal.
        // The selectedTaskId scroll-into-view effect (below) also avoids
        // scrollIntoView() to prevent unwanted horizontal jumps.

        // Post-render: replace milestone bars with diamond markers + critical path + baselines
        setTimeout(() => {
          if (containerRef.current) {
            patchMilestoneDiamonds(containerRef.current);
            patchCriticalPath(containerRef.current, frappeTasks, showCriticalPath);
            patchCriticalPathArrows(containerRef.current, frappeTasks, showCriticalPath);
            patchBaselineGhostBars(containerRef.current, ganttRef.current, baselineTasks, showBaseline);
          }
        }, 50);
        scrollToToday();
      } catch (err) {
        console.error('[GanttChart] Frappe Gantt init failed:', err);
        ganttRef.current = null;
      }
    }
  }, [frappeTasks, destroyChart, scrollToToday, showCriticalPath]);
  // Note: viewMode intentionally excluded — view mode switching is
  // handled by its own useEffect below. Including it here causes
  // a redundant refresh() that conflicts with change_view_mode().

  // ── View Mode Switch ─────────────────────────────────────────
  useEffect(() => {
    if (ganttRef.current && viewMode !== prevViewModeRef.current) {
      try {
        ganttRef.current.change_view_mode(viewMode, true);
        // Re-apply milestone diamonds + critical path + baselines after view mode change
        setTimeout(() => {
          if (containerRef.current) {
            patchMilestoneDiamonds(containerRef.current);
            patchCriticalPath(containerRef.current, frappeTasks, showCriticalPath);
            patchCriticalPathArrows(containerRef.current, frappeTasks, showCriticalPath);
            patchBaselineGhostBars(containerRef.current, ganttRef.current, baselineTasks, showBaseline);
          }
        }, 50);
        scrollToToday();
      } catch (err) {
        console.warn('[GanttChart] view mode switch failed:', err);
      }
      prevViewModeRef.current = viewMode;
    }
  }, [viewMode]);

  // ── DEV-565: Critical path toggle ──────────────────────────
  // Re-apply critical path styling when the toggle changes
  // without requiring a full chart re-render.
  useEffect(() => {
    if (!containerRef.current || !ganttRef.current) return;
    patchCriticalPath(containerRef.current, frappeTasks, showCriticalPath);
    patchCriticalPathArrows(containerRef.current, frappeTasks, showCriticalPath);
  }, [showCriticalPath, frappeTasks]);

  // ── DEV-564: Baseline ghost bars toggle ───────────────────
  useEffect(() => {
    if (!containerRef.current || !ganttRef.current) return;
    patchBaselineGhostBars(containerRef.current, ganttRef.current, baselineTasks, showBaseline);
  }, [showBaseline, baselineTasks]);

  // ── Selected bar highlight ───────────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return;
    // Remove previous selection
    containerRef.current
      .querySelectorAll('.bar-wrapper.selected')
      .forEach((el) => el.classList.remove('selected'));

    if (selectedTaskId && ganttRef.current) {
      // Frappe Gantt uses the task id as a data attribute on bar groups
      const bar = containerRef.current.querySelector(
        `.bar-wrapper[data-id="${selectedTaskId}"]`
      );
      if (bar) bar.classList.add('selected');
    }
  }, [selectedTaskId, frappeTasks]);

  // ── Scroll selected bar into view (vertical only) ───────────
  // When the user clicks a task in the grid, scroll the Gantt chart
  // vertically so the bar is visible, but preserve the current
  // horizontal scroll position. Using scrollIntoView() would move
  // both axes and trigger Frappe's scroll handler, causing the
  // timescale to jump unexpectedly.
  useEffect(() => {
    if (!selectedTaskId || !containerRef.current) return;
    const timer = setTimeout(() => {
      if (!containerRef.current) return;
      const gc = containerRef.current.querySelector('.gantt-container') as HTMLElement | null;
      if (!gc) return;
      const bar = gc.querySelector(
        `.bar-wrapper[data-id="${selectedTaskId}"] .bar`
      ) as SVGElement | null;
      if (!bar) return;

      // Calculate the bar's vertical position relative to .gantt-container
      const barRect = bar.getBoundingClientRect();
      const gcRect = gc.getBoundingClientRect();
      const barTopInContainer = barRect.top - gcRect.top + gc.scrollTop;

      // Only scroll vertically if the bar is outside the visible area
      const visibleTop = gc.scrollTop;
      const visibleBottom = gc.scrollTop + gc.clientHeight;
      const barCenter = barTopInContainer + barRect.height / 2;

      if (barCenter < visibleTop || barCenter > visibleBottom) {
        gc.scrollTop = Math.max(0, barTopInContainer - gc.clientHeight / 2);
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [selectedTaskId]);

  // ── Render ───────────────────────────────────────────────────
  // The empty space below the SVG is filled by CSS background-color
  // on .ganttChartWrapper, matched to the grid row color via theme
  // variables. No SVG DOM manipulation needed.
  const themeClass = colorTheme === 'black'
    ? styles.themeBlack
    : colorTheme === 'white'
    ? styles.themeWhite
    : '';

  return (
    <div className={`${styles.ganttChartWrapper} ${themeClass}`.trim()}>
      <div ref={containerRef} className={styles.ganttContainer} />
      {frappeTasks.length === 0 && (
        <div className={styles.noData}>
          <p>No tasks with dates to display</p>
        </div>
      )}
    </div>
  );
}
